import { useState, useEffect, useCallback } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { useSupabaseContext } from "../lib/supabase_context";
import { PUBLIC_STORE_PROFILES } from "../lib/public_store_profiles";

const STORAGE_KEY = "bbp_recently_viewed";
const MAX_ITEMS = 20;

export type RecentlyViewedItem = {
  id: string;
  name: string | null;
  price: number | null;
  markdown: number | null;
  mainImageUrl: string | null;
  storeId: string | null;
  storeName: string | null;
  viewedAt: number;
};

function normalizeItem(raw: unknown): RecentlyViewedItem | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  const id = o.id != null ? String(o.id) : "";
  if (!id) return null;
  const storeId =
    o.storeId != null && o.storeId !== "" ? String(o.storeId) : null;
  return {
    id,
    name: typeof o.name === "string" ? o.name : null,
    price: typeof o.price === "number" ? o.price : null,
    markdown: typeof o.markdown === "number" ? o.markdown : null,
    mainImageUrl: typeof o.mainImageUrl === "string" ? o.mainImageUrl : null,
    storeId,
    storeName: typeof o.storeName === "string" ? o.storeName : null,
    viewedAt: typeof o.viewedAt === "number" ? o.viewedAt : Date.now(),
  };
}

function readFromStorage(): RecentlyViewedItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map(normalizeItem)
      .filter((x): x is RecentlyViewedItem => x != null);
  } catch {
    return [];
  }
}

function writeToStorage(items: RecentlyViewedItem[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // localStorage full or unavailable — silently ignore
  }
}

/** Merge two lists by product id, keeping the entry with the latest viewedAt. */
function mergeByLatestViewed(
  a: RecentlyViewedItem[],
  b: RecentlyViewedItem[],
): RecentlyViewedItem[] {
  const map = new Map<string, RecentlyViewedItem>();
  for (const i of [...a, ...b]) {
    const k = String(i.id);
    const existing = map.get(k);
    if (!existing || i.viewedAt > existing.viewedAt) {
      map.set(k, i);
    }
  }
  return Array.from(map.values()).sort((a, b) => b.viewedAt - a.viewedAt);
}

/** Keep entries whose store is still in the public ACTIVE storefront list. */
async function filterToActiveStorefronts(
  supabase: SupabaseClient,
  items: RecentlyViewedItem[],
): Promise<RecentlyViewedItem[]> {
  const storeIds = [
    ...new Set(
      items
        .map((i) => i.storeId)
        .filter((id): id is string => Boolean(id))
        .map((id) => String(id)),
    ),
  ];
  if (storeIds.length === 0) {
    return items;
  }

  const { data, error } = await supabase
    .from(PUBLIC_STORE_PROFILES)
    .select("id")
    .in("id", storeIds);

  if (error || !data) {
    return items;
  }

  const active = new Set(data.map((row) => String(row.id)));
  const filtered = items.filter(
    (i) => !i.storeId || active.has(String(i.storeId)),
  );

  // Avoid wiping the list when ids were mismatched (e.g. number vs string from JSON) or lookup quirks
  if (filtered.length === 0 && items.length > 0) {
    return items;
  }
  return filtered;
}

export function useRecentlyViewed() {
  const { supabase } = useSupabaseContext();
  const [items, setItems] = useState<RecentlyViewedItem[]>([]);

  // Hydrate from localStorage, then drop products from inactive / non-public stores
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const raw = readFromStorage();
      const filtered = await filterToActiveStorefronts(supabase, raw);
      if (cancelled) return;
      setItems(filtered);
      if (JSON.stringify(filtered) !== JSON.stringify(raw)) {
        writeToStorage(filtered);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase]);

  const addToRecentlyViewed = useCallback(
    (item: Omit<RecentlyViewedItem, "viewedAt">) => {
      const normalized: Omit<RecentlyViewedItem, "viewedAt"> = {
        ...item,
        id: String(item.id),
        storeId: item.storeId != null ? String(item.storeId) : null,
      };
      setItems((prev) => {
        // `prev` is often still [] on the product page because hydrate runs async
        // in the same effect phase as add — merge with disk so we never wipe history.
        const base = mergeByLatestViewed(readFromStorage(), prev);
        const filtered = base.filter((i) => String(i.id) !== normalized.id);
        const updated = [
          { ...normalized, viewedAt: Date.now() },
          ...filtered,
        ].slice(0, MAX_ITEMS);

        void filterToActiveStorefronts(supabase, updated).then((next) => {
          setItems(next);
          writeToStorage(next);
        });

        writeToStorage(updated);
        return updated;
      });
    },
    [supabase],
  );

  return { recentlyViewed: items, addToRecentlyViewed };
}
